/**
 * Server-side companion: `^[a-zA-Z0-9.'\- ]*$` on user name at create and edit.
 *
 * Allowed characters:
 *   • letters: a-z, A-Z
 *   • digits: 0-9
 *   • punctuation: `.` `'` `-`
 *   • whitespace: space
 *
 * Empty / nullish values are considered valid — required-ness is enforced separately by the caller.
 */
export const USER_NAME_REGEX: RegExp = /^[a-zA-Z0-9.'\- ]*$/

export const USER_NAME_INVALID_MESSAGE =
  "User names may only contain letters (a–z, A–Z), numbers (0–9), period (.), apostrophe ('), hyphen (-), and spaces."

/**
 * Per-field cap for first / last name. IMDB stores them as separate `varchar(32)`
 * columns on `practitioner`, so each input is bounded on its own rather than a
 * combined display name being split to find the parts.
 */
export const NAME_FIELD_MAX_LENGTH = 32

/**
 * `practitioner.middle_name` is `varchar(64)`, twice the width of first / last.
 * Sharing the 32 cap silently blocked typing at half the allowed length.
 */
export const MIDDLE_NAME_FIELD_MAX_LENGTH = 64

export const NAME_FIELD_TOO_LONG_MESSAGE = `First and last name must each be ${NAME_FIELD_MAX_LENGTH} characters or fewer, and middle name ${MIDDLE_NAME_FIELD_MAX_LENGTH} or fewer.`

/** Display name cap (`users.name`); also the width of `practitioner.full_name`. */
export const DISPLAY_NAME_MAX_LENGTH = 64

/** Join the name parts into the display name the API stores and Auth0 shows. */
export function composeDisplayName(
  first: string | null | undefined,
  middle?: string | null,
  last?: string | null
): string {
  return [first, middle, last]
    .map((part) => (part ?? '').trim())
    .filter((part) => part.length > 0)
    .join(' ')
}

/** True when a single name field exceeds its column width. */
export function nameFieldTooLong(value: string | null | undefined, maxLength: number = NAME_FIELD_MAX_LENGTH): boolean {
  return (value ?? '').trim().length > maxLength
}

/** True when the middle name exceeds its own, wider column. */
export function middleNameFieldTooLong(value: string | null | undefined): boolean {
  return nameFieldTooLong(value, MIDDLE_NAME_FIELD_MAX_LENGTH)
}

/** Length error for one name field, or undefined when it fits. */
export function nameFieldLengthError(value: string | null | undefined): string | undefined {
  return nameFieldTooLong(value) ? NAME_FIELD_TOO_LONG_MESSAGE : undefined
}

/** Length error for the middle name field, or undefined when it fits. */
export function middleNameFieldLengthError(value: string | null | undefined): string | undefined {
  return middleNameFieldTooLong(value) ? NAME_FIELD_TOO_LONG_MESSAGE : undefined
}

/** True when `value` is empty/nullish or contains only allowlisted characters. */
export function isValidUserName(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return USER_NAME_REGEX.test(value)
}

/**
 * Validates a name field on edit: unchanged values are accepted so an NPI-only profile
 * update is not blocked by a legacy value that predates the stricter regex (e.g. an
 * email used as a name). Length is checked separately and always applies to new input,
 * since a legacy over-length value cannot be stored anyway.
 */
export function isValidUserNameIfChanged(value: string, baseline: string): boolean {
  if (value.trim() === baseline.trim()) return true
  return isValidUserName(value)
}

/** Returns the standard validation message when `value` contains disallowed characters, otherwise undefined. */
export function userNameError(value: string | null | undefined): string | undefined {
  return isValidUserName(value) ? undefined : USER_NAME_INVALID_MESSAGE
}

/** Like `userNameError`, but skips validation when `value` matches `baseline`. */
export function userNameErrorIfChanged(value: string, baseline: string): string | undefined {
  return isValidUserNameIfChanged(value, baseline) ? undefined : USER_NAME_INVALID_MESSAGE
}
